import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Bell, CheckCheck, Calendar, Wallet, CreditCard, ShieldCheck, ArrowRightCircle, Info,
} from "lucide-react";

const ICONS = {
  booking_created: Calendar,
  milestone_advanced: ArrowRightCircle,
  payment_received: CreditCard,
  provider_verified: ShieldCheck,
  escrow_released: Wallet,
  message: Info,
};

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.put("/v1/notifications/read-all");
    load();
  };

  const openChange = (v) => {
    setOpen(v);
    if (v) load();
  };

  return (
    <Popover open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild>
        <button className="relative grid place-items-center h-10 w-10 rounded-full hover:bg-[#E9F5F9] transition-colors duration-200" data-testid="notification-bell">
          <Bell size={20} className="text-[#0D5C75]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F97316] text-white text-[10px] font-bold grid place-items-center" data-testid="notification-count">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-xl overflow-hidden" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E7EC] bg-[#F7F9FC]">
          <span className="font-heading font-bold text-sm text-[#101828]">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-[#0D5C75] hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
              <CheckCheck size={13} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No notifications yet</div>
          ) : items.map((n) => {
            const Icon = ICONS[n.type] || Info;
            return (
              <div key={n.id} className={`flex gap-3 px-4 py-3 border-b border-[#F0F2F5] ${n.read ? "" : "bg-[#E9F5F9]/50"}`} data-testid={`notification-${n.id}`}>
                <span className="grid place-items-center h-8 w-8 rounded-lg bg-[#E9F5F9] text-[#0D5C75] shrink-0"><Icon size={16} /></span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#101828]">{n.title}</div>
                  <div className="text-xs text-muted-foreground">{n.body}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
